import {
  MAX_DECIMAL_DIGITS,
  MAX_DECIMAL_SCALE,
  MAX_RENDERER_DECIMAL_ABS,
} from "./constants";

declare const exactDecimalBrand: unique symbol;
declare const nonNegativeDecimalBrand: unique symbol;
declare const positiveDecimalBrand: unique symbol;

/** Exact base-10 decimal with no exponent, separators, or surrounding text. */
export type DecimalString = string & {
  readonly [exactDecimalBrand]: true;
};

export type NonNegativeDecimalString = DecimalString & {
  readonly [nonNegativeDecimalBrand]: true;
};

export type PositiveDecimalString = NonNegativeDecimalString & {
  readonly [positiveDecimalBrand]: true;
};

export interface ExactDecimal {
  readonly coefficient: bigint;
  readonly scale: number;
}

export type DecimalToNumberFailureReason =
  | "invalid_decimal"
  | "non_finite"
  | "out_of_renderer_range"
  | "precision_loss";

export type DecimalToNumberResult =
  | {
      readonly success: true;
      readonly value: number;
    }
  | {
      readonly success: false;
      readonly value: null;
      readonly reason: DecimalToNumberFailureReason;
    };

const DECIMAL_PATTERN = /^(-?)(\d+)(?:\.(\d+))?$/;

/**
 * Parses a bounded decimal string into an exact integer coefficient.
 * `null` means the value is outside the persisted contract.
 */
export function parseDecimalString(value: unknown): ExactDecimal | null {
  if (typeof value !== "string") {
    return null;
  }

  const match = DECIMAL_PATTERN.exec(value);
  if (!match) {
    return null;
  }

  const integerPart = match[2] ?? "";
  const fractionalPart = match[3] ?? "";
  const digitCount = integerPart.length + fractionalPart.length;

  if (
    digitCount > MAX_DECIMAL_DIGITS ||
    fractionalPart.length > MAX_DECIMAL_SCALE
  ) {
    return null;
  }

  const unsignedCoefficient = BigInt(`${integerPart}${fractionalPart}`);
  const isNegative = match[1] === "-";

  // Negative zero is intentionally excluded because its sign has no financial
  // meaning and would give one value two signed representations.
  if (isNegative && unsignedCoefficient === 0n) {
    return null;
  }

  return {
    coefficient: isNegative ? -unsignedCoefficient : unsignedCoefficient,
    scale: fractionalPart.length,
  };
}

export function isDecimalString(value: unknown): value is DecimalString {
  return parseDecimalString(value) !== null;
}

export function isNonNegativeDecimalString(
  value: unknown,
): value is NonNegativeDecimalString {
  const parsed = parseDecimalString(value);
  return parsed !== null && parsed.coefficient >= 0n;
}

export function isPositiveDecimalString(
  value: unknown,
): value is PositiveDecimalString {
  const parsed = parseDecimalString(value);
  return parsed !== null && parsed.coefficient > 0n;
}

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(exponent);
}

function alignCoefficient(decimal: ExactDecimal, scale: number): bigint {
  return decimal.coefficient * powerOfTen(scale - decimal.scale);
}

export function compareDecimalStrings(
  left: DecimalString,
  right: DecimalString,
): -1 | 0 | 1 {
  const parsedLeft = parseDecimalString(left);
  const parsedRight = parseDecimalString(right);

  if (!parsedLeft || !parsedRight) {
    throw new TypeError("유효한 decimal string만 비교할 수 있습니다.");
  }

  const scale = Math.max(parsedLeft.scale, parsedRight.scale);
  const difference =
    alignCoefficient(parsedLeft, scale) - alignCoefficient(parsedRight, scale);
  return difference < 0n ? -1 : difference > 0n ? 1 : 0;
}

export function formatDecimal({
  coefficient,
  scale,
}: ExactDecimal): DecimalString {
  if (!Number.isInteger(scale) || scale < 0 || scale > MAX_DECIMAL_SCALE) {
    throw new RangeError("decimal scale이 허용 범위를 벗어났습니다.");
  }

  const negative = coefficient < 0n;
  const digits = (negative ? -coefficient : coefficient).toString();
  const padded = scale > 0 ? digits.padStart(scale + 1, "0") : digits;
  const integerPart = scale > 0 ? padded.slice(0, -scale) : padded;
  const rawFraction = scale > 0 ? padded.slice(-scale) : "";
  const fraction = rawFraction.replace(/0+$/, "");
  const sign = negative && coefficient !== 0n ? "-" : "";
  const value = `${sign}${integerPart}${fraction ? `.${fraction}` : ""}`;

  if (!isDecimalString(value)) {
    throw new RangeError("decimal 결과가 저장 범위를 벗어났습니다.");
  }

  return value;
}

export function normalizeDecimalString(value: DecimalString): DecimalString {
  const parsed = parseDecimalString(value);
  if (!parsed) {
    throw new TypeError("유효한 decimal string만 정규화할 수 있습니다.");
  }
  return formatDecimal(parsed);
}

export function addDecimalStrings(
  values: readonly DecimalString[],
): DecimalString {
  if (values.length === 0) {
    return "0" as DecimalString;
  }

  const parsed = values.map(parseDecimalString);
  if (parsed.some((value) => value === null)) {
    throw new TypeError("유효한 decimal string만 더할 수 있습니다.");
  }

  const decimals = parsed as ExactDecimal[];
  const scale = Math.max(...decimals.map((value) => value.scale));
  const coefficient = decimals.reduce(
    (sum, value) => sum + alignCoefficient(value, scale),
    0n,
  );

  return formatDecimal({ coefficient, scale });
}

function reduceDecimalScale(decimal: ExactDecimal): ExactDecimal {
  let { coefficient, scale } = decimal;
  while (scale > 0 && coefficient % 10n === 0n) {
    coefficient /= 10n;
    scale -= 1;
  }
  return { coefficient, scale };
}

/**
 * Exact multiplication. Products that cannot fit back into the persisted
 * 30-digit/12-scale decimal contract fail instead of being rounded.
 */
export function multiplyDecimalStrings(
  left: DecimalString,
  right: DecimalString,
): DecimalString {
  const parsedLeft = parseDecimalString(left);
  const parsedRight = parseDecimalString(right);

  if (!parsedLeft || !parsedRight) {
    throw new TypeError("유효한 decimal string만 곱할 수 있습니다.");
  }

  const product = reduceDecimalScale({
    coefficient: parsedLeft.coefficient * parsedRight.coefficient,
    scale: parsedLeft.scale + parsedRight.scale,
  });

  if (product.scale > MAX_DECIMAL_SCALE) {
    throw new RangeError("decimal 곱셈 결과의 소수 자릿수가 범위를 벗어났습니다.");
  }

  return formatDecimal(product);
}

function expandExponentialNumber(value: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(value);
  if (!match) {
    return value;
  }

  const sign = match[1] ?? "";
  const integerPart = match[2] ?? "0";
  const fractionalPart = match[3] ?? "";
  const exponent = Number(match[4]);
  const digits = `${integerPart}${fractionalPart}`;
  const decimalIndex = integerPart.length + exponent;

  if (decimalIndex <= 0) {
    return `${sign}0.${"0".repeat(-decimalIndex)}${digits}`;
  }
  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${"0".repeat(decimalIndex - digits.length)}`;
  }
  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
}

/**
 * Converts a persisted decimal at the renderer boundary without silently
 * dropping decimal digits. Human decimals such as 0.1 pass because JS emits
 * the same shortest round-trip decimal; values whose decimal representation
 * changes (for example 999999999999999.99) fail with `precision_loss`.
 */
export function safeDecimalToNumber(value: unknown): DecimalToNumberResult {
  const parsed = parseDecimalString(value);
  if (!parsed) {
    return { success: false, value: null, reason: "invalid_decimal" };
  }

  const normalized = formatDecimal(parsed);
  const numberValue = Number(normalized);
  if (!Number.isFinite(numberValue)) {
    return { success: false, value: null, reason: "non_finite" };
  }
  if (Math.abs(numberValue) > MAX_RENDERER_DECIMAL_ABS) {
    return {
      success: false,
      value: null,
      reason: "out_of_renderer_range",
    };
  }

  const roundTripText = expandExponentialNumber(String(numberValue));
  if (
    !isDecimalString(roundTripText) ||
    !decimalStringsEqual(normalized, roundTripText)
  ) {
    return { success: false, value: null, reason: "precision_loss" };
  }

  return { success: true, value: numberValue };
}

export function decimalStringsEqual(
  left: DecimalString,
  right: DecimalString,
): boolean {
  return compareDecimalStrings(left, right) === 0;
}
